import {
  getIsInBlogSlot,
  addWidgetToSlot,
  addWidgetToEditor,
  handleEditFormClick,
  type HandleEditFormClickParams,
} from './utils';
import type {
  ComponentRef,
  EditorSDK,
  Origin,
} from '@wix/editor-platform-sdk-types';
import type { OwnerLogger, FlowAPI } from '@wix/yoshi-flow-editor';
import { reportPublishedWidgets, reportDeletedWidget } from './bi';
import { maybeInstallEcom } from './ecom';
import { maybeInstallScheduling } from './scheduling';

interface OnWidgetAddedParams {
  sdk: EditorSDK;
  appToken: string;
  flowApi: FlowAPI;
  origin?: Origin;
}

export const widgetAddedHandler = async ({
  sdk,
  appToken,
  flowApi,
  componentRef,
  originalComponentId,
  origin,
}: {
  sdk: EditorSDK;
  appToken: string;
  flowApi: FlowAPI;
  componentRef: ComponentRef;
  originalComponentId?: string;
  origin?: Origin;
}) => {
  const component = await sdk.document.components.serialize(appToken, {
    componentRef,
  });

  const isInBlogSlot = await getIsInBlogSlot(appToken, sdk, component);
  if (isInBlogSlot) {
    await addWidgetToSlot({
      appToken,
      sdk,
      componentRef,
      component,
      flowApi,
    });
  } else {
    await addWidgetToEditor({
      appToken,
      sdk,
      componentRef,
      component,
      flowApi,
      originalComponentId,
      origin,
    });
  }
};

export const onWidgetAdded = ({
  sdk,
  appToken,
  flowApi,
  origin,
}: OnWidgetAddedParams) =>
  sdk.addEventListener('widgetAdded', async (props) => {
    const {
      detail: { componentRef, originalComponentId },
    } = props;
    await widgetAddedHandler({
      sdk,
      appToken,
      flowApi,
      componentRef,
      originalComponentId,
      origin,
    });
  });

interface OnComponentDeletedParams {
  sdk: EditorSDK;
  bi?: OwnerLogger;
  appToken: string;
}

export const onComponentDeleted = ({
  sdk,
  bi,
  appToken,
}: OnComponentDeletedParams) =>
  sdk.addEventListener(
    'componentDeleted',
    async ({ detail: { componentRef } }) => {
      const msid = await sdk.info.getMetaSiteId(appToken);
      reportDeletedWidget({ componentRef, bi, msid });
    },
  );

interface OnSitePublishedParams {
  sdk: EditorSDK;
  appToken: string;
  bi?: OwnerLogger;
}

export const onSitePublished = ({ sdk, appToken, bi }: OnSitePublishedParams) =>
  sdk.addEventListener('siteWasPublished', async () => {
    await reportPublishedWidgets({ sdk, appToken, bi });
  });

interface OnAppVisitedInDashboardParams {
  sdk: EditorSDK;
  flowApi: FlowAPI;
}

export const onAppVisitedInDashboard = ({
  sdk,
  flowApi,
}: OnAppVisitedInDashboardParams) => {
  sdk.addEventListener('appVisitedInDashboard', async () => {
    await Promise.all([
      maybeInstallEcom({ sdk, flowApi }),
      maybeInstallScheduling({ sdk, flowApi }),
    ]);
  });
};

export const onEditFormClicked = ({
  panorama,
  appToken,
  editorSDK,
  httpClient,
  errorHandler,
}: Omit<HandleEditFormClickParams, 'componentRef'>) =>
  // @ts-expect-error
  editorSDK.addEventListener('edit-form-clicked', async (props) => {
    const componentRef = props.detail.editorPointer.documentPointer;

    panorama?.transaction('open_edit_form_modal').start();
    handleEditFormClick({
      componentRef,
      appToken,
      editorSDK,
      httpClient,
      errorHandler,
      panorama,
    });
  });
